import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  Grid,
  Paper,
  alpha,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CustomButton from "../../../../components/common/button/CustomButton";
import "../../storageComponent/StorageComponent.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllSitesAction,
  setAddSiteResponse,
} from "../../../../store/reducers/storageSettingSlice";
import { Site } from "./site.type";
import ManageSiteFilter from "./ManageSiteFilter";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import { FirstPage, LastPage } from "@mui/icons-material";
import { useTheme } from "@emotion/react";

interface SiteListProps {
  storages: Site[];
  selectedSiteId?: string;
  setSelectedSiteId?: (id: string) => void;
  onAddNew: () => void;
  filterType: "all" | "available";
  setFilterType: (type: "all" | "available") => void;
}

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  search: string;
  siteType: string;
  storageType: string;
}

const ROW_HEIGHT = 50;
const MIN_LIST_LIMIT = 10;
const SEARCH_DELAY = 500;

const SiteList: React.FC<SiteListProps> = ({
  storages,
  selectedSiteId,
  setSelectedSiteId,
  onAddNew,
  filterType,
  setFilterType,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  //  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isScreenLarge = useMediaQuery("(max-width:1248px)");
  const {
    isAddSiteSuccess,
    siteListTotalSites,
    siteListAvailableSites,
    sitePagination,
  } = useSelector((state: any) => state.storageSetting);

  const getResponsiveLimit = () => {
    if (typeof window === "undefined") {
      return 10;
    }

    return Math.max(
      MIN_LIST_LIMIT,
      Math.floor(window.innerHeight / ROW_HEIGHT),
    );
  };

  const [search, setSearch] = useState("");
  // Filter modal state
  const [openFilter, setOpenFilter] = useState(false);
  const [filterOption, setFilterOption] = useState<FilterOption>({
    page: 1,
    limit: getResponsiveLimit(),
    orderBy: "created_at",
    orderIn: "DESC",
    search: "",
    siteType: "",
    storageType: "",
  });

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilterOption((prev) => ({
        ...prev,
        page: 1,
        search,
      }));
    }, SEARCH_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  /**
   * Main Site API
   *
   * This will be called when:
   * - search changes after debounce
   * - siteType changes
   * - storageType changes
   * - pagination changes
   * - sorting changes
   */
  useEffect(() => {
    if (
      !filterOption.siteType &&
      !filterOption.storageType &&
      !filterOption.search
    ) {
      dispatch(getAllSitesAction({ filterOption, status: filterType }));
    }
  }, [dispatch, filterOption, filterType]);

  /**
   * Refresh after Add Site
   */
  useEffect(() => {
    if (isAddSiteSuccess) {
      dispatch(getAllSitesAction({ filterOption, status: filterType }));
      dispatch(setAddSiteResponse(false));
    }
  }, [dispatch, isAddSiteSuccess, filterOption, filterType]);

  /**
   * Apply filter from modal
   */
  const handleApplyFilter = (data: any) => {
    setFilterOption((prev) => ({
      ...prev,
      page: 1,
      siteType: data.siteType,
      storageType: data.storageType,
    }));
  };

  /**
   * Clear filter
   */
  const handleClearFilter = () => {
    setFilterOption((prev) => ({
      ...prev,
      page: 1,
      siteType: "",
      storageType: "",
    }));
  };

  /**
   * Close filter modal
   */
  const handleHideFilter = () => {
    setOpenFilter(false);
  };

  const handleFilter = () => {
    setOpenFilter(true);
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    setFilterOption((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const getOccupancy = (totalUnits: number, availableUnits: number) => {
    const total = Number(totalUnits) || 0;
    const available = Number(availableUnits) || 0;

    if (total <= 0) {
      return 0;
    }

    const occupied = Math.max(0, total - available);
    return Math.min(100, Math.round((occupied / total) * 100));
  };

  return (
    <Box className="storage-list-container">
      {/* Header Buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          marginBottom: "10px",
          justifyContent: "space-between",
        }}
      >
        <CustomButton
          style={{ fontSize: "12px", padding: "4px 8px" }}
          title="Add New"
          onClick={onAddNew}
        />

        <CustomButton
          style={{ fontSize: "12px", padding: "4px 8px" }}
          title="Filter"
          variant="outlined"
          onClick={handleFilter}
        />
      </Box>

      {/* Filter Modal */}
      {openFilter && (
        <ManageSiteFilter
          open={openFilter}
          title="Filter Sites"
          onHide={handleHideFilter}
          filterData={{
            siteType: filterOption.siteType,
            storageType: filterOption.storageType,
          }}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        />
      )}

      {/* Filter Tabs */}
      <Box
        sx={{
          background: "#fff",
          fontSize: "12px",
          fontWeight: 500,
          marginBottom: "10px",
          padding: "7px",
          border: "1px solid #C4CDD5",
          display: "flex",
          gap: "8px",
        }}
      >
        <Button
          fullWidth
          onClick={() => setFilterType("all")}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            margin: "0 4px",
            minHeight: "12px",
            height: "32px",
            backgroundColor: filterType === "all" ? "#02906f" : "#f0f0f0",
            color: filterType === "all" ? "#fff" : "inherit",
            fontWeight: filterType === "all" ? 600 : 400,
            fontSize: "12px",
            "&:hover": {
              backgroundColor: filterType === "all" ? "#02906f" : "#e0e0e0",
            },
          }}
        >
          All ({siteListTotalSites ?? 0})
        </Button>

        <Button
          fullWidth
          onClick={() => setFilterType("available")}
          sx={{
            textTransform: "none",
            borderRadius: "4px",
            margin: "0 4px",
            minHeight: "12px",
            height: "32px",
            backgroundColor: filterType === "available" ? "#02906f" : "#f0f0f0",
            color: filterType === "available" ? "#fff" : "inherit",
            fontWeight: filterType === "available" ? 600 : 400,
            fontSize: "12px",
            "&:hover": {
              backgroundColor:
                filterType === "available" ? "#02906f" : "#e0e0e0",
            },
          }}
        >
          Available ({siteListAvailableSites ?? 0})
        </Button>
      </Box>

      {/* Search Box */}
      <TextField
        fullWidth
        placeholder="Search"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
        size="small"
        sx={{
          marginBottom: "10px",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
          },
          "& input": {
            padding: "4px",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  color: "#637381",
                  fontSize: "18px",
                }}
              />
            </InputAdornment>
          ),
        }}
      />

      {/* Table Header */}
      <Grid
        container
        spacing={0}
        alignItems="center"
        sx={{
          background: "#E5F4F1",
          color: "#637381",
          padding: "10px",
          marginBottom: "4px",
          border: "1px solid #C4CDD5",
        }}
      >
        <Grid
          item
          xs={6}
          sx={{
            borderRight: "1px solid #C4CDD5",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Site Name
          </Typography>
        </Grid>

        <Grid
          item
          xs={6}
          sx={{
            textAlign: "left",
            paddingLeft: "5px",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Site Type<br/>
            (Occ)
          </Typography>
        </Grid>
        
      </Grid>

      {/* Storage List */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          paddingBottom: 2,
          overflowY: "auto",
        }}
      >
        {storages.length > 0 ? (
          storages.map((item: any) => (
            <Paper
              key={item.id}
              elevation={1}
              sx={{
                border: `1px solid ${
                  selectedSiteId === item.id
                    ? item.completedUnits === item.totalUnits
                      ? "#008e73"
                      : "#02906f"
                    : "#ddd"
                }`,
                display: "flex",
                alignItems: "center",
                p: "2px",
                backgroundColor:
                  selectedSiteId === item.id ? alpha("#02906f", 0.15) : "#fff",
                position: "relative",
                marginBottom: "4px",
                cursor: "pointer",
              }}
              onClick={() => {
                setSelectedSiteId?.(item.id);
              }}
            >
              {/* Left Accent */}
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 10,
                  backgroundColor:
                    item.completedUnits === item.totalUnits
                      ? "#008e73"
                      : "#02906f",
                }}
              />

              {/* Content */}
              <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                <Grid container spacing={0} sx={{ minWidth: 0 }}>
                  <Grid item xs={6} sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        lineHeight: 1.5,
                        textTransform: "capitalize",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item.site_name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {item.postcode}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sx={{
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {item.site_type}<br/>
                      ({(() => {
                      const occupancy = getOccupancy(
                        item.totalUnits,
                        item.availableUnits,
                      );

                      return (
                        <Typography
                        component="span"
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            color: occupancy >= 70 ? "#d32f2f" : "#02906f",
                          }}
                        >
                          {occupancy}%
                        </Typography>
                      );
                    })()})
                    </Typography>
                  </Grid>
                  
                </Grid>
              </Box>
            </Paper>
          ))
        ) : (
          <Box
            sx={{
              padding: "40px 20px",
              textAlign: "center",
              color: "#999",
            }}
          >
            <Typography>No storages found</Typography>
          </Box>
        )}
        {storages.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: 2,
              alignItems: "center",
              borderTop: "2px solid #d5d5d5",
            }}
          >
            <Button
              size="small"
              onClick={() => handlePageChange(sitePagination?.currentPage - 1)}
              variant="contained"
              color="secondary"
              sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
              disabled={sitePagination?.currentPage === 1}
            >
              <FirstPage sx={{ fontSize: "16px" }} />
            </Button>
            <Typography
              sx={{
                fontSize: "12px",
                textAlign: "center",
              }}
            >
              {sitePagination?.totalItems} listings found. Page{" "}
              {sitePagination?.currentPage} of {sitePagination?.totalPages}.
            </Typography>
            <Button
              onClick={() => handlePageChange(sitePagination?.currentPage + 1)}
              variant="contained"
              color="secondary"
              sx={{ padding: "0px", height: "20px", minWidth: "30px" }}
              disabled={!sitePagination?.hasNextPage}
            >
              <LastPage sx={{ fontSize: "16px" }} />
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SiteList;
